<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { onMount } from 'svelte';

    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import ButtonSecondary from '../../shared/ButtonSecondary.svelte';
    import CheckboxLikeFilter from '../../shared/CheckboxLikeFilter.svelte';
    import ErrorColor from '../../shared/ErrorColor.svelte';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import { api } from '../../../stores/api';
    import UsageLimitBar from './UsageLimitBar.svelte';
    import { UsageStatsApp } from './UsageStatsApp';

    let usageApp: UsageStatsApp = new UsageStatsApp($api);

    /**
     * Konta wygasle sa domyslnie ukryte - poza miesiacami, w ktorych cos robily (patrz
     * isRowVisible w UsageStatsApp). Stan zostaje lokalny w zakladce: stores/filter.ts obsluguje
     * filtry list roboczych, a to jest jednorazowy przelacznik panelu admina.
     */
    let showExpired = false;

    onMount(() => {
        usageApp.load().then(() => {
            usageApp = usageApp;
        });
    });

    /**
     * Pierwsze przypisanie odswieza widok na `isLoadingMore() === true` (metoda ustawia flage
     * synchronicznie, przed pierwszym await), drugie - po dociagnieciu miesiaca.
     */
    async function loadNextMonth(): Promise<void> {
        const pending = usageApp.loadNextMonth();
        usageApp = usageApp;
        await pending;
        usageApp = usageApp;
    }
</script>

<div class="usage-stats">
    {#if usageApp.isLoading()}
        <div class="loading">
            <p>{$_('Loading usage data...')}</p>
            <Spinner />
        </div>
    {:else}
        {#if usageApp.hasError()}
            <MessageDanger error={usageApp.getError() || ''} />
        {/if}
        <!-- legenda opisuje format wszystkich tabel naraz, wiec stoi RAZ nad pierwsza sekcja -->
        {#if usageApp.getUniqueMonths().length > 0}
            <div class="controls">
                <p class="legend">
                    {$_('Usage is shown as used / limit. ∞ means no limit; the Limits enforced column tells whether exceeding is blocked.')}
                </p>
                <CheckboxLikeFilter label={$_('Show expired accounts')} bind:checked={showExpired} />
            </div>
        {/if}
        {#each usageApp.getUniqueMonths() as month}
            <div class="usage-item">
                <h3>{month}</h3>
                <div class="table-scroll">
                    <table class="table-usage">
                        <thead>
                            <tr class="usage-item">
                                <th>{$_('Customer ID')}</th>
                                <th>{$_('Customer')}</th>
                                <th title={$_('Whether exceeding the limit is blocked for this account')}>
                                    {$_('Limits enforced')}
                                </th>
                                <!--                                <th>{$_('User e-mail')}</th>-->
                                <th title={$_('Maximum number of locations in a month')}>
                                    {$_('Max locations')}
                                </th>
                                <th title={$_('Maximum number of routes in a month')}>
                                    {$_('Max routes')}
                                </th>
                                <th title={$_('Number of routing transactions in a month (route requests logged in routing_log)')}>
                                    {$_('Transactions')}
                                </th>
                                <th title={$_('Number of exported files (CSV/XLSX)')}>
                                    {$_('Exports')}
                                </th>
                                <th
                                    title={$_(
                                        'Number of routes recalculated manually (the same route may be counted more than once, e.g. if a user recalculates one route 4 times, the count is 4)'
                                    )}
                                >
                                    {$_('Manual recalculations')}
                                </th>
                                <th
                                    title={$_(
                                        'Number of routes recalculated automatically (the same route may be counted more than once, e.g. if the schedule runs 3 times a month over 10 routes, the count is 30 (3x10))'
                                    )}
                                >
                                    {$_('Automatic recalculations')}
                                </th>
                                <th
                                    title={$_(
                                        'Total number of recalculated routes (manual + automatic) (the same route may be counted more than once)'
                                    )}
                                >
                                    {$_('Recalculations total')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- filtr miesiaca i widocznosci konta siedzi w getVisibleRows(), zeby
                                 szablon nie musial rozstrzygac, czy sekcja ma jeszcze jakis wiersz -->
                            {#each usageApp.getVisibleRows(month, showExpired) as usageRow}
                                <tr class="usage-item">
                                    <td>#{usageRow.customer_id}</td>
                                    <td title={usageRow.user_email}>
                                        {usageRow.customer_name}
                                        {#if usageRow.is_expired}
                                            <span class="badge-expired" title={usageRow.expiration_date ?? ''}>
                                                <ErrorColor>{$_('EXPIRED')}</ErrorColor>
                                            </span>
                                        {/if}
                                    </td>
                                    <!--                                        <td>{usageRow.user_email}</td>-->
                                    <!-- stan konfiguracji konta, nie metryka miesiaca: mowi, czy limity
                                         w trzech kolumnach obok cokolwiek BLOKUJA -->
                                    <td>{usageRow.limits_enforced ? $_('Yes') : $_('No')}</td>
                                    <td class="cell-usage">
                                        <UsageLimitBar value={usageRow.location_max} limit={usageRow.location_limit} />
                                    </td>
                                    <td class="cell-usage">
                                        <UsageLimitBar value={usageRow.connection_max} limit={usageRow.connection_limit} />
                                    </td>
                                    <td class="cell-usage">
                                        <UsageLimitBar value={usageRow.transaction_count} limit={usageRow.transaction_limit} />
                                    </td>
                                    <td>{usageRow.export_sum}</td>
                                    <td>{usageRow.recalc_manual}</td>
                                    <td>{usageRow.recalc_auto}</td>
                                    <td>{usageRow.recalc_sum}</td>
                                </tr>
                            {:else}
                                <!-- wszystkie konta tego miesiaca odsiane: bez tego wiersza zostalby
                                     sam naglowek tabeli, nieodrozialny od bledu ladowania -->
                                <tr class="usage-item">
                                    <td class="cell-empty" colspan="10">
                                        {$_('No active accounts in this month')}
                                    </td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            </div>
        {:else}
            {#if !usageApp.hasError()}
                <p>{$_('No usage data available')}</p>
            {/if}
        {/each}
        <!-- bez dolnego limitu - cofac sie mozna dowolnie daleko, wiec przycisk jest zawsze widoczny -->
        <div class="load-more">
            <ButtonSecondary text={$_('Load next month')} disabled={usageApp.isLoadingMore()} showIcon={false} on:click={loadNextMonth} />
            {#if usageApp.isLoadingMore()}
                <Spinner />
            {/if}
        </div>
    {/if}
</div>

<style>
    .usage-stats {
        display: flex;
        flex-direction: column;
        row-gap: 20px;
    }
    .loading,
    .load-more {
        display: flex;
        align-items: center;
        column-gap: 20px;
    }
    .legend {
        margin: 0;
        flex: 1 1 auto;
        font-size: 0.85rem;
        opacity: 0.8;
    }
    /* dziesiec kolumn nie miesci sie na wezszych ekranach - przewija sie tabela, nie strona */
    .table-scroll {
        overflow-x: auto;
    }
    .table-usage {
        width: 100%;
        border-collapse: collapse;
    }
    .usage-item th {
        /*white-space: nowrap;*/
        padding: 0.5rem;
        border: 1px solid #ddd;
        text-align: right;
        font-weight: 200;
        opacity: 0.8;
        white-space: normal;
        overflow-wrap: break-word;
    }
    .usage-item td {
        padding: 0.5rem;
        border: 1px solid #ddd;
        text-align: right;
    }
    .controls {
        display: flex;
        align-items: center;
        column-gap: 16px;
        row-gap: 8px;
        flex-wrap: wrap;
    }
    .badge-expired {
        margin-left: 6px;
        font-size: 0.75rem;
        white-space: nowrap;
    }
    .usage-item td.cell-empty {
        text-align: left;
        opacity: 0.7;
    }
    /* komorka z paskiem: liczby czyta sie od lewej, a pasek potrzebuje szerokosci na sensowna skale */
    .usage-item td.cell-usage {
        text-align: left;
        min-width: 140px;
    }
</style>
